// Signing in through identity with PKCE, and keeping the session: the Blazor app's AuthService and
// CustomAuthenticationStateProvider. The tokens stay under the keys they had, so a session opened in the Blazor
// cabinet carries on here.

import { config } from './config.ts';
import { send } from './http.ts';
import { log } from './logs.ts';
import { redact } from './redact.ts';
import { emit, state } from './state.ts';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const VERIFIER_KEY = 'pkce_verifier';
const REDIRECT_URI_KEY = 'pkce_redirect_uri';

interface TokenAnswer {
    access_token?: string;
    refresh_token?: string;
}

function read(storage: Storage, key: string): string | null {
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
}

function write(storage: Storage, key: string, value: string | null | undefined): void {
    try {
        if (value === null || value === undefined) storage.removeItem(key);
        else storage.setItem(key, value);
    } catch {
        // Storage refused: the session lasts as long as this page.
    }
}

export const getAccessToken = (): string | null => read(localStorage, TOKEN_KEY);

const getRefreshToken = (): string | null => read(localStorage, REFRESH_TOKEN_KEY);

/** A token present is a session, as the Blazor app judged it; the API says when one no longer holds. */
export const isAuthenticated = (): boolean => !!(getAccessToken() || '').trim();

function storeTokens(accessToken: string, refreshToken: string | undefined): void {
    write(localStorage, TOKEN_KEY, accessToken);

    if (refreshToken && refreshToken.trim()) write(localStorage, REFRESH_TOKEN_KEY, refreshToken);

    emit('authChanged');
}

function base64Url(bytes: Uint8Array): string {
    let binary = '';

    for (const byte of bytes) binary += String.fromCharCode(byte);

    return btoa(binary).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

async function pkce(): Promise<{ verifier: string; challenge: string }> {
    const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

    return { verifier, challenge: base64Url(new Uint8Array(hash)) };
}

const callbackUrl = (): string => `${location.origin}/identity-signin`;

/** Off to identity's login, to come back to /identity-signin and then to where the person was. */
export async function initiateLogin(): Promise<void> {
    const { verifier, challenge } = await pkce();

    write(sessionStorage, VERIFIER_KEY, verifier);
    write(sessionStorage, REDIRECT_URI_KEY, callbackUrl());

    location.href = config.identity.publicUrl
        + `?client_id=${config.identity.clientId}`
        + '&scope=user'
        + '&response_type=code'
        + `&redirect_uri=${encodeURIComponent(callbackUrl())}`
        + `&state=${encodeURIComponent(location.href)}`
        + `&code_challenge=${encodeURIComponent(challenge)}`
        + '&code_challenge_method=S256';
}

/**
 * The code identity sent back, exchanged for tokens; then the account's language, which wins over the one picked
 * here - or, when the account has none, is set to it. Answers whether a session was opened.
 */
export async function loginCallback(code: string, onLanguage: (language: string) => Promise<void>): Promise<boolean> {
    const verifier = read(sessionStorage, VERIFIER_KEY);
    const redirectUri = read(sessionStorage, REDIRECT_URI_KEY);

    write(sessionStorage, VERIFIER_KEY, null);
    write(sessionStorage, REDIRECT_URI_KEY, null);

    const response = await send(config.identity.tokenUrl, {
        method: 'POST',
        formUrlEncoded: {
            grant_type: 'authorization_code',
            client_id: config.identity.clientId,
            code_verifier: verifier ?? '',
            code,
            redirect_uri: redirectUri && redirectUri.trim() ? redirectUri : null
        }
    });

    if (!response.ok) {
        log.error(`PKCE token exchange failed. Status: ${response.status}, Response: ${redact(response.text.slice(0, 2000))}`,
            undefined, undefined, 'Lify.Influencer.Services.Realization.AuthService');

        return false;
    }

    const tokens = response.json<TokenAnswer>();

    if (!tokens || !tokens.access_token || !String(tokens.access_token).trim()) {
        log.error('PKCE token exchange returned empty or invalid token payload', undefined, undefined,
            'Lify.Influencer.Services.Realization.AuthService');

        return false;
    }

    storeTokens(tokens.access_token, tokens.refresh_token);

    const language = await send(`${config.mainApiUrl.replace(/\/$/, '')}/lify/account/language`, { bearer: tokens.access_token });

    if (!language.ok) {
        log.warn(`Fetching stored language failed. Status: ${language.status}`, undefined,
            'Lify.Influencer.Services.Realization.AuthService');

        return true;
    }

    const stored = language.string().trim();

    if (stored) {
        if (stored !== state.locale) await onLanguage(stored);
    } else if (state.locale) {
        await setAccountLanguage(state.locale);
    }

    return true;
}

/** The language on the account, so pushes and emails follow the one chosen here. */
export async function setAccountLanguage(language: string): Promise<void> {
    const response = await send(
        `${config.mainApiUrl.replace(/\/$/, '')}/lify/account/language?language=${encodeURIComponent(language)}`,
        { method: 'PATCH', bearer: getAccessToken(), body: '' }
    );

    if (!response.ok) {
        log.warn(`Failed to set language ${language}. Status: ${response.status}, Response: ${redact(response.text.slice(0, 500))}`,
            undefined, 'Lify.Influencer.Services.Realization.AuthService');
    }
}

let refreshing: Promise<boolean> | null = null;

/**
 * A new access token for an expired one. One refresh at a time however many requests met the 401: a refresh token
 * spent twice is refused the second time.
 */
export function refreshToken(): Promise<boolean> {
    if (refreshing) return refreshing;

    const attempt = (async (): Promise<boolean> => {
        const refresh = getRefreshToken();

        if (!refresh || !refresh.trim()) return false;

        const response = await send(config.identity.tokenUrl.replace('/token', '/refresh-token'), {
            method: 'POST',
            formUrlEncoded: {
                access_token: getAccessToken() ?? '',
                refresh_token: refresh,
                client_id: config.identity.clientId
            }
        });

        if (!response.ok) {
            log.warn(`Token refresh failed. Status: ${response.status}, Response: ${redact(response.text.slice(0, 500))}`,
                undefined, 'Lify.Influencer.Services.Realization.AuthService');

            return false;
        }

        const tokens = response.json<TokenAnswer>();

        if (!tokens || !tokens.access_token || !String(tokens.access_token).trim()) {
            log.warn('Token refresh returned empty payload', undefined, 'Lify.Influencer.Services.Realization.AuthService');

            return false;
        }

        storeTokens(tokens.access_token, tokens.refresh_token);

        return true;
    })().finally(() => {
        refreshing = null;
    });

    refreshing = attempt;

    return attempt;
}

/** Forgets the session in this browser. The push row on the server is the caller's to drop first, while it can. */
export function clearSession(): void {
    write(localStorage, TOKEN_KEY, null);
    write(localStorage, REFRESH_TOKEN_KEY, null);
    state.person = null;
    emit('authChanged');
}
